import React from 'react';
import {
    Box,
    VStack,
    HStack,
    Text,
    Button,
    Icon,
    Badge,
    InputGroup,
    InputRightElement,
    NumberInput,
    NumberInputField,
    Switch,
    Tooltip,
    Flex,
} from '@chakra-ui/react';
import { FiSend, FiGift } from 'react-icons/fi';
import { useAuction } from '../context/AuctionContext';

const ContributionBox = ({
    amount,
    handleAmountChange,
    handlePresetContribution,
    handleContributionTypeSelect,
    handleClaim,
    handleAllDaysContribution,
    isWalletConnected,
    hasTotalContribution,
}) => {
    const { isDemo, toggleDemoMode } = useAuction();

    // Preset contribution amounts with emojis and colors
    const presetAmounts = [
        { amount: 50, rank: "Blue Whale", emoji: "🐳", color: "#ff1f69" },
        { amount: 30, rank: "Whale", emoji: "🐋", color: "#ff9f1f" },
        { amount: 20, rank: "Shark", emoji: "🦈", color: "#ffe248" },
        { amount: 10, rank: "Dolphin", emoji: "🐬", color: "#7ed957" },
        { amount: 5, rank: "Fish", emoji: "🐟", color: "#38b6ff" },
        { amount: 3, rank: "Tropical Fish", emoji: "🐠", color: "#fd2626" },
        { amount: 2, rank: "Blowfish", emoji: "🐡", color: "#ff914d" },
        { amount: 1, rank: "Octopus", emoji: "🐙", color: "#ffde59" },
        { amount: 0.5, rank: "Lobster", emoji: "🦞", color: "#2fdb88" },
        { amount: 0.1, rank: "Shrimp", emoji: "🦐", color: "#159fef" },
    ];

    return (
        <Box
            bg="rgba(16, 16, 16, 0.9)"
            borderRadius="xl"
            p={6}
            border="1px solid rgba(153, 69, 255, 0.3)"
            position="relative"
            overflow="hidden"
            boxShadow="0 0 40px rgba(153, 69, 255, 0.3)"
            _before={{
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bg: "radial-gradient(circle at center, rgba(153, 69, 255, 0.15), transparent 70%)",
                pointerEvents: "none",
                zIndex: 0
            }}
        >
            <Box position="relative" zIndex={1}>
                <VStack spacing={6} align="stretch">
                    <Flex justify="space-between" align="center">
                        <HStack>
                            <Icon as={FiSend} boxSize={{ base: 4, sm: 5 }} color="purple.400" />
                            <Text fontSize={{ base: "lg", sm: "xl" }} fontWeight="bold" color="white">Contribute</Text>
                        </HStack>
                        
                        <Tooltip label={isDemo ? "Demo Mode (No real SOL transactions)" : "Real Mode (Requires SOL)"}>
                            <HStack spacing={2}>
                                <Text fontSize="sm" color="gray.400">Demo</Text>
                                <Switch 
                                    colorScheme="purple" 
                                    isChecked={!isDemo}
                                    onChange={toggleDemoMode}
                                    size="md"
                                />
                                <Text fontSize="sm" color="gray.400">Real</Text>
                            </HStack>
                        </Tooltip>
                    </Flex>

                    <VStack spacing={4}>
                        <HStack spacing={2} width="100%" justify="center" flexWrap="wrap" gap={1}>
                            {presetAmounts.map((preset, index) => (
                                <Button
                                    key={index}
                                    variant="outline"
                                    onClick={() => handlePresetContribution(preset.amount)}
                                    _hover={{ 
                                        transform: "scale(1.05)",
                                        borderColor: preset.color,
                                        bg: 'transparent'
                                    }}
                                    transition="all 0.2s"
                                    px={3}
                                    py={2}
                                    isDisabled={!isWalletConnected}
                                    color={preset.color}
                                    borderColor={preset.color}
                                    fontSize="0.95em"
                                    minW="120px"
                                    borderRadius="md"
                                >
                                    <HStack spacing={1}>
                                        <Text fontSize="xs">{preset.amount}+ SOL</Text>
                                        <Text>{preset.emoji}</Text>
                                    </HStack>
                                </Button>
                            ))}
                        </HStack>

                        <InputGroup size={{ base: "md", md: "lg" }}>
                            <NumberInput
                                value={amount}
                                onChange={(valueString) => handleAmountChange(valueString)}
                                min={0}
                                max={100000}
                                precision={2}
                                width="100%"
                            >
                                <NumberInputField
                                    placeholder="Enter amount"
                                    borderRadius="full"
                                    borderWidth="2px"
                                    borderColor="gray.300"
                                    _hover={{ borderColor: "gray.400" }}
                                    _focus={{ borderColor: "blue.400", boxShadow: "none" }}
                                    height={{ base: "40px", md: "48px" }}
                                    fontSize={{ base: "sm", md: "md" }}
                                    isDisabled={!isWalletConnected}
                                />
                            </NumberInput>
                            <InputRightElement width="4rem" height="100%">
                                <Text color="gray.400" fontSize={{ base: "sm", md: "md" }}>SOL</Text>
                            </InputRightElement>
                        </InputGroup>

                        <HStack spacing={4} width="100%">
                            <Button
                                variant="outline"
                                size={{ base: "md", md: "lg" }}
                                flex={1}
                                borderColor="green.400"
                                color="green.400"
                                _hover={{
                                    bg: 'green.400',
                                    color: 'white'
                                }}
                                borderRadius="full"
                                borderWidth="2px"
                                height={{ base: "40px", md: "48px" }}
                                fontSize={{ base: "sm", md: "md" }}
                                fontWeight="bold"
                                leftIcon={<Icon as={FiSend} boxSize={{ base: 4, sm: 5 }} />}
                                onClick={() => handleAllDaysContribution()}
                                isDisabled={!isWalletConnected || !amount}
                                px={4}
                            >
                                All Days
                            </Button>

                            <Button
                                flex={1}
                                bg="transparent"
                                border="2px solid"
                                borderColor="#0099ff"
                                color="#0099ff"
                                _hover={{
                                    bg: '#0099ff',
                                    color: 'white',
                                }}
                                leftIcon={<FiGift boxSize={{ base: 4, sm: 5 }} />}
                                onClick={handleClaim}
                                isDisabled={!hasTotalContribution}
                                size={{ base: "md", md: "lg" }}
                                rounded="full"
                                px={4}
                                height={{ base: "40px", md: "48px" }}
                                fontSize={{ base: "sm", md: "md" }}
                                fontWeight="bold"
                            >
                                Claim
                            </Button>
                        </HStack>
                    </VStack>
                </VStack>
            </Box>
        </Box>
    );
};

export default ContributionBox;
